import { useState, useEffect, useCallback } from 'react';
import { getQuota, getSubscription } from '../api';
import type { QuotaResponse, SubscriptionResponse } from '../types';

interface UseQuotaReturn {
  /** Current quota data */
  quota: QuotaResponse | null;
  /** Current subscription data */
  subscription: SubscriptionResponse['subscription'] | null;
  /** Whether quota is loading */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether instance has an active subscription */
  hasSubscription: boolean;
  /** Whether runtime quota is exhausted */
  isQuotaExhausted: boolean;
  /** Whether quota is low (< 10% remaining) */
  isQuotaLow: boolean;
  /** Percentage of runtime quota used (0-100) */
  runtimeUsedPercent: number;
  /** Formatted remaining time string */
  remainingTimeFormatted: string;
  /** Refresh quota from server */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing instance billing quota.
 *
 * Fetches quota and subscription data for an instance.
 * Provides computed values for UI display:
 * - isQuotaExhausted: cannot start/continue running
 * - isQuotaLow: show warning (< 10% remaining)
 * - runtimeUsedPercent: for progress bar
 */
export function useQuota(instanceId: string | null): UseQuotaReturn {
  const [quota, setQuota] = useState<QuotaResponse | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionResponse['subscription'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadQuota = useCallback(async () => {
    if (!instanceId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch quota and subscription in parallel
      const [quotaResult, subResult] = await Promise.all([
        getQuota(instanceId),
        getSubscription(instanceId),
      ]);

      setQuota(quotaResult);
      setSubscription(subResult.subscription);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load quota';
      console.error('[useQuota] Failed to load quota:', message);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  // Load quota on mount and when instanceId changes
  useEffect(() => {
    loadQuota();
  }, [loadQuota]);

  // Computed values
  const hasSubscription = subscription?.status === 'active';

  const isQuotaExhausted = quota
    ? !quota.hasQuota || quota.runtime.remaining <= 0
    : false;

  // Low quota: less than 10% remaining
  const runtimeUsedPercent = quota && quota.runtime.limit > 0
    ? Math.min(100, Math.round((quota.runtime.used / quota.runtime.limit) * 100))
    : 0;

  const isQuotaLow = quota && quota.runtime.limit > 0
    ? quota.runtime.remaining < quota.runtime.limit * 0.1
    : false;

  // Format remaining time
  const formatRemainingTime = (seconds: number): string => {
    if (seconds <= 0) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  const remainingTimeFormatted = quota
    ? formatRemainingTime(quota.runtime.remaining)
    : '-';

  return {
    quota,
    subscription,
    loading,
    error,
    hasSubscription,
    isQuotaExhausted,
    isQuotaLow,
    runtimeUsedPercent,
    remainingTimeFormatted,
    refresh: loadQuota,
  };
}
